import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {noop} from 'lodash';
import {
  ExpressionBinding,
  ExpressionBindMetaPayload,
  ExpressionEditorActionPayload,
} from '@packages/systems/ExpressionEditor-utils';
import {Expressions, matchExpression} from '@packages/systems/wfdl';
import {EList, ERecord, EText, Expression} from '@packages/systems/wfdl-ast';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  Button,
  CloseDefaultIcon,
  IconButton,
  View,
  Text,
  HStack,
  Popover as SpringPopover,
  PopoverTrigger,
  PopoverContent,
  SearchableList,
  Checkbox,
  Divider,
  SearchableListBox,
  SearchableListInput,
  Select,
  Skeleton,
  Spinner,
  TextInput,
  VStack,
  Spacer as SpringSpacer,
} from '@packages/systems/spring';
import {List} from 'immutable';
import type {Map as ImmutableMap} from 'immutable';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import {isProductsCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  getDisplayableFields,
  getFilterableFields,
  getFilterOperatorsForField,
  buildFieldFiltersQueryString,
} from './curatedPickerFields';
import type {CuratedPickerFilterOperator} from './curatedPickerFields';
import {getFieldDisplayText} from './getFieldDisplayText';
import PropertyList, {
  PropertyListAddButton,
  PropertyListItem,
  PropertyListItemID,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {identity} from '@packages/utilities/fp/utils';
import useDebouncedValue from '@packages/systems/ui/hooks/useDebouncedValue';
import {
  useCollectionItemsPage,
  useReferencedCollectionItems,
} from '@packages/systems/cms/client';
import type {GetCollectionItemsResponse} from '@packages/systems/cms/types';
import type {Locale} from '@packages/systems/localization-types';
import {deleteAt, moveElement} from '@packages/utilities/array';
import {getItemLabel} from '@packages/shared/utils/ItemUtils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {selectCollectionState} from '@packages/systems/cms/client-selectors';
import {SymbolBindingDot} from '@packages/systems/ui/shared-ui/SymbolBindingDot';
import {rightSidebarLabelWidth} from '@packages/systems/design-system/constants';
import {getId} from '@packages/systems/core/utils/RecordUtils';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {selectCurrentLocale} from '@packages/systems/localization';
import {dynListSettingsAnalyticsEventOccurred} from '@packages/systems/dynamo/actions/DynListSettingsActionCreators';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';

const getCurrentLocale: (
  state: DesignerStores
) => Locale | null | undefined = ({LocalizationStore}) =>
  selectCurrentLocale(LocalizationStore);

const selectTimezone = (state: DesignerStores): string | undefined =>
  state.DataManagerUIStore?.timezone ?? undefined;

const DISPLAY_FIELD_STORAGE_PREFIX = 'wf.curatedPicker.displayField.';
const DISPLAY_FIELD_NONE_VALUE = '__none__';

const BOOL_FILTER_VALUE_OPTIONS = [
  {value: 'true', label: 'Yes'},
  {value: 'false', label: 'No'},
];

type FilterConditionState = {
  id: number;
  fieldSlug: string | null;
  operator: CuratedPickerFilterOperator['value'] | null;
  value: string;
};

const getFilterValueInputType = (
  field: FieldType
): 'date' | 'number' | 'text' => {
  switch (field.get('type')) {
    case 'Date':
      return 'date';
    case 'Number':
      return 'number';
    default:
      return 'text';
  }
};

const readStoredDisplayFieldSlug = (collectionId: string): string | null => {
  try {
    return window.localStorage.getItem(
      DISPLAY_FIELD_STORAGE_PREFIX + collectionId
    );
  } catch {
    return null;
  }
};

const writeStoredDisplayFieldSlug = (
  collectionId: string,
  slug: string | null
): void => {
  try {
    if (slug) {
      window.localStorage.setItem(
        DISPLAY_FIELD_STORAGE_PREFIX + collectionId,
        slug
      );
    } else {
      window.localStorage.removeItem(
        DISPLAY_FIELD_STORAGE_PREFIX + collectionId
      );
    }
  } catch {
    // localStorage can be unavailable (e.g. privacy modes) — the selector
    // still works for the session, it just won't persist.
  }
};

const RIGHT_SIDEBAR_WIDTH = 225;
const MAX_CURATED_ITEMS_ALLOWED = 100;
const CURATED_SETTINGS_LABEL = 'Select items';
const UPDATE_EXPRESSION_ACTION_TYPE = 'EXPRESSION_ACTION';

function getDataAutomationId(identifier: string): string {
  return `collection-list-settings-${identifier}`;
}

function parseDynListCuratedIds(expr: Expression): string[] {
  const list = getItemIdsExprFromCuratedExpr(expr);
  if (!list) {
    return [];
  }

  return list.reduce((curatedIds: string[], itemExpr) => {
    const curatedItemId = Expressions.getText(itemExpr);

    if (curatedItemId) {
      curatedIds.push(curatedItemId);
    }

    return curatedIds;
  }, []);
}

function getItemIdsExprFromCuratedExpr(expr: Expression): Expression[] | null {
  const record = Expressions.getRecord(expr);
  if (!record) {
    return null;
  }
  const itemIdsExpr = record.itemIds;
  return itemIdsExpr ? Expressions.getList(itemIdsExpr) : null;
}

function createCCLExpr(itemIds: string[]): Expression {
  return ERecord({
    itemIds: EList(itemIds.map((itemId) => EText(itemId))),
  });
}

export const DynListCuratedSettings = ({
  collectionId,
  defaultValueExpr,
  expr,
  exprAsUnique,
  expressionEditorDispatch,
}: {
  collectionId: string;
  defaultValueExpr: Expression;
  expr: ContextualExpression;
  exprAsUnique: ContextualExpression;
  expressionEditorDispatch: (action: ExpressionEditorActionPayload) => void;
}) => {
  const currentLocale = useDesignerSelector(getCurrentLocale);
  const collectionState = useDesignerSelector(selectCollectionState);
  const timezone = useDesignerSelector(selectTimezone);
  const collection = collectionState.collections.get(collectionId);
  const [searchInputValue, setSearchInputValue] = useState('');
  const debouncedSearchQuery = useDebouncedValue({
    value: searchInputValue,
    wait: 150,
  });

  // The /commerce/products endpoint takes over for non-itemRef formats on
  // Products collections and returns a different shape, so those collections
  // keep the lightweight format and skip the display-field feature.
  const isProducts = isProductsCollection(collection);
  const itemsFormat = isProducts ? 'itemRef' : 'withoutHeavyFields';

  const collectionFields: List<FieldType> = collection?.fields ?? List();
  const displayableFields = useMemo(
    () =>
      isProducts ? List<FieldType>() : getDisplayableFields(collectionFields),
    [isProducts, collectionFields]
  );

  const [displayFieldSlug, setDisplayFieldSlug] = useState<string | null>(() =>
    readStoredDisplayFieldSlug(collectionId)
  );
  const displayField = useMemo(
    () =>
      displayFieldSlug
        ? (displayableFields.find(
            (field) => field.get('slug') === displayFieldSlug
          ) ?? null)
        : null,
    [displayableFields, displayFieldSlug]
  );

  const handleDisplayFieldChange = useCallback(
    (newValue: string) => {
      const slug = newValue === DISPLAY_FIELD_NONE_VALUE ? null : newValue;
      setDisplayFieldSlug(slug);
      writeStoredDisplayFieldSlug(collectionId, slug);
    },
    [collectionId]
  );

  const displayFieldSelectOptions = useMemo(
    () => [
      {value: DISPLAY_FIELD_NONE_VALUE, label: 'None'},
      ...displayableFields
        .map((field) => ({
          value: field.get('slug') as string,
          label: field.get('name') as string,
        }))
        .toArray(),
    ],
    [displayableFields]
  );

  // --- filter state (ephemeral — resets when the popover closes) ---
  // Unlike the display field, filtering works for Products collections too:
  // filters ride the `fields[...]` params on the regular items endpoint,
  // which Products keeps using under the itemRef format.
  const filterableFields = useMemo(
    () => getFilterableFields(collectionFields),
    [collectionFields]
  );
  const [isFilterRowOpen, setIsFilterRowOpen] = useState(false);
  const [filterConditions, setFilterConditions] = useState<
    Array<FilterConditionState>
  >([]);
  const nextConditionIdRef = useRef(0);

  const makeEmptyCondition = useCallback((): FilterConditionState => {
    return {
      id: nextConditionIdRef.current++,
      fieldSlug: null,
      operator: null,
      value: '',
    };
  }, []);

  const handleFilterToggleClick = useCallback(() => {
    setIsFilterRowOpen((prevOpen) => !prevOpen);
    setFilterConditions((prev) =>
      prev.length === 0 ? [makeEmptyCondition()] : prev
    );
  }, [makeEmptyCondition]);

  const updateFilterCondition = useCallback(
    (conditionId: number, patch: Partial<FilterConditionState>) => {
      setFilterConditions((prev) =>
        prev.map((condition) =>
          condition.id === conditionId ? {...condition, ...patch} : condition
        )
      );
    },
    []
  );

  const handleAddConditionClick = useCallback(() => {
    setFilterConditions((prev) => [...prev, makeEmptyCondition()]);
  }, [makeEmptyCondition]);

  const handleRemoveConditionClick = useCallback((conditionId: number) => {
    setFilterConditions((prev) =>
      prev.filter((condition) => condition.id !== conditionId)
    );
  }, []);

  const resolveFilterField = useCallback(
    (fieldSlug: string | null) =>
      fieldSlug
        ? (filterableFields.find((field) => field.get('slug') === fieldSlug) ??
          null)
        : null,
    [filterableFields]
  );

  const filterQueryString = useMemo(
    () =>
      buildFieldFiltersQueryString(
        filterConditions.map((condition) => ({
          field: resolveFilterField(condition.fieldSlug),
          operator: condition.operator,
          value: condition.value,
        })),
        {timezone}
      ),
    [filterConditions, resolveFilterField, timezone]
  );
  const hasActiveFilter = filterQueryString !== '';

  // Debounce so typing in a text/number filter value doesn't fire a request
  // per keystroke; bypass on clear so the unfiltered result shows immediately.
  const debouncedFilterQueryString = useDebouncedValue({
    value: filterQueryString,
    wait: 150,
  });

  const resetFilter = useCallback(() => {
    setIsFilterRowOpen(false);
    setFilterConditions([]);
  }, []);

  const {data: searchableListQueryResponse, isFetching: isSearchFetching} =
    useCollectionItemsPage(collection, {
      format: itemsFormat,
      // Bypass debounce on clear so the cached unfiltered result renders immediately
      query: searchInputValue === '' ? '' : debouncedSearchQuery,
      fields: filterQueryString === '' ? '' : debouncedFilterQueryString,
      locales: currentLocale ? [currentLocale] : undefined,
    });
  const isSearchInFlight =
    (searchInputValue !== '' && searchInputValue !== debouncedSearchQuery) ||
    isSearchFetching;
  const hasActiveSearch = searchInputValue !== '';

  const dispatch = useDispatch();
  const dispatchAnalyticsEvent = useCallback(
    (eventName: string) => {
      dispatch(
        dynListSettingsAnalyticsEventOccurred({
          collectionId,
          eventName,
        })
      );
    },
    [collectionId, dispatch]
  );

  // Track filter activation as a transition so typing in the value input
  // doesn't fire an analytics event per keystroke.
  const prevHasActiveFilterRef = useRef(false);
  useEffect(() => {
    if (hasActiveFilter && !prevHasActiveFilterRef.current) {
      dispatchAnalyticsEvent(
        ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_FILTER_APPLIED
      );
    }
    prevHasActiveFilterRef.current = hasActiveFilter;
  }, [hasActiveFilter, dispatchAnalyticsEvent]);

  const handleClearFilterClick = useCallback(() => {
    // Clearing keeps the filter section open with one empty condition so the
    // user can immediately build a new filter.
    setFilterConditions([makeEmptyCondition()]);
    dispatchAnalyticsEvent(
      ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_FILTER_CLEARED
    );
  }, [makeEmptyCondition, dispatchAnalyticsEvent]);

  const filterFieldSelectOptions = useMemo(
    () =>
      filterableFields
        .map((field) => ({
          value: field.get('slug') as string,
          label: field.get('name') as string,
        }))
        .toArray(),
    [filterableFields]
  );

  const handleConditionFieldChange = useCallback(
    (conditionId: number, newValue: string) => {
      const nextField = resolveFilterField(newValue);
      updateFilterCondition(conditionId, {
        fieldSlug: newValue,
        operator: nextField
          ? getFilterOperatorsForField(nextField)[0]!.value
          : null,
        value: '',
      });
    },
    [resolveFilterField, updateFilterCondition]
  );

  const getOptionValueSelectOptions = useCallback((field: FieldType) => {
    const options = field.getIn(['validations', 'options'], List()) as List<
      ImmutableMap<string, string>
    >;
    return options
      .map((option) => ({
        // Option definitions key their identifier as `id` (older data may
        // use `_id`) — mirror RecordUtils.getId's `_id || id` fallback.
        value: option.get('_id') ?? option.get('id') ?? '',
        label: option.get('name', ''),
      }))
      .toArray();
  }, []);

  const dispatchCuratedExpressionUpdate = useCallback(
    (newExpression: Expression) => {
      expressionEditorDispatch({
        type: UPDATE_EXPRESSION_ACTION_TYPE,
        key: [],
        payload: {
          newExpression,
        },
      });
    },
    [expressionEditorDispatch]
  );

  const curatedExpr = useMemo(() => stripAll(expr), [expr]);
  const curatedIds = useMemo(() => {
    const parsedIds = parseDynListCuratedIds(curatedExpr);

    if (parsedIds && Array.isArray(parsedIds) && parsedIds.length > 0) {
      return parsedIds;
    }
    return [];
  }, [curatedExpr]);

  const curatedItemsQueries = useReferencedCollectionItems(
    collection && curatedIds.length > 0
      ? {
          [collectionId]: curatedIds,
        }
      : {},
    {
      format: itemsFormat,
      locales: currentLocale ? [currentLocale] : undefined,
    }
  );

  const isCuratedDataLoading = curatedItemsQueries.some(
    (query) => query.isLoading
  );

  // Build a lookup once so we don't need to search every item for each selected ID.
  const curatedItemsById = useMemo(() => {
    const itemsById = new Map<
      string,
      GetCollectionItemsResponse['items'][number]
    >();

    for (const query of curatedItemsQueries) {
      for (const item of query.data?.items ?? []) {
        itemsById.set(getId(item), item);
      }
    }

    return itemsById;
  }, [curatedItemsQueries]);

  const handleDeleteCuratedItemClick = useCallback(
    (index: PropertyListItemID) => {
      const list = getItemIdsExprFromCuratedExpr(curatedExpr);
      const currentList = deleteAt(list || [], Number(index));

      dispatchCuratedExpressionUpdate(ERecord({itemIds: EList(currentList)}));
      dispatchAnalyticsEvent(
        ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_ITEMS_REORDERED
      );
    },
    [dispatchCuratedExpressionUpdate, curatedExpr, dispatchAnalyticsEvent]
  );

  const handleReorderCuratedItemDrag = useCallback(
    ({
      itemId,
      dropTargetId,
    }: {
      itemId: PropertyListItemID;
      dropTargetId: PropertyListItemID;
    }) => {
      const list = getItemIdsExprFromCuratedExpr(curatedExpr);
      const currentList = moveElement(
        list || [],
        Number(itemId),
        Number(dropTargetId)
      );

      dispatchCuratedExpressionUpdate(ERecord({itemIds: EList(currentList)}));
      dispatchAnalyticsEvent(
        ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_ITEMS_REORDERED
      );
    },
    [dispatchCuratedExpressionUpdate, curatedExpr, dispatchAnalyticsEvent]
  );

  const handleDeleteAllCuratedItemsClick = useCallback(() => {
    dispatchCuratedExpressionUpdate(ERecord({itemIds: EList([])}));
    dispatchAnalyticsEvent(
      ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_REMOVE_ALL_BUTTON_CLICKED
    );
  }, [dispatchCuratedExpressionUpdate, dispatchAnalyticsEvent]);

  const itemLabelCache = useMemo(() => new Map<string, string>(), []);

  const getItemLabelCacheKey = useCallback(
    (itemId: string) => {
      return currentLocale ? `${currentLocale._id}-${itemId}` : itemId;
    },
    [currentLocale]
  );

  const searchableListOptions = useMemo(() => {
    return searchableListQueryResponse.items.map((item) => {
      const label = getItemLabel(item, collectionState.scheduledItems);
      const itemId = getId(item);
      const cacheKey = getItemLabelCacheKey(itemId);
      const cachedLabel = itemLabelCache.get(cacheKey);

      if (!cachedLabel || cachedLabel !== label) {
        itemLabelCache.set(cacheKey, label);
      }

      const description = displayField
        ? getFieldDisplayText(
            displayField,
            item.get(displayField.get('slug')),
            {
              timezone,
            }
          )
        : undefined;

      return {
        label,
        value: itemId,
        description,
        // Render the description on its own line so the title and the field
        // value each get the full row width.
        isMultiline: description !== undefined,
      };
    });
  }, [
    searchableListQueryResponse.items,
    collectionState.scheduledItems,
    getItemLabelCacheKey,
    itemLabelCache,
    displayField,
    timezone,
  ]);

  const handleSelectAllCuratedItemsClick = useCallback(() => {
    const allIds = searchableListOptions
      .map((option) => option.value)
      .slice(0, MAX_CURATED_ITEMS_ALLOWED);
    dispatchCuratedExpressionUpdate(createCCLExpr(allIds));
    dispatchAnalyticsEvent(
      ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_SELECT_ALL_BUTTON_CLICKED
    );
  }, [
    searchableListOptions,
    dispatchCuratedExpressionUpdate,
    dispatchAnalyticsEvent,
  ]);

  // Compare by membership rather than count — with a search or filter active,
  // selected items can fall outside the visible options, and a bare count
  // comparison would show the checkbox as fully selected when it isn't.
  const selectedVisibleOptionCount = useMemo(() => {
    const curatedIdSet = new Set(curatedIds);
    return searchableListOptions.filter((option) =>
      curatedIdSet.has(option.value)
    ).length;
  }, [curatedIds, searchableListOptions]);

  const isAllSelected =
    searchableListOptions.length > 0 &&
    selectedVisibleOptionCount === searchableListOptions.length;
  const isIndeterminate = curatedIds.length > 0 && !isAllSelected;

  const handleToggleSelectAll = useCallback(() => {
    if (curatedIds.length > 0) {
      handleDeleteAllCuratedItemsClick();
    } else {
      handleSelectAllCuratedItemsClick();
    }
  }, [
    curatedIds.length,
    handleDeleteAllCuratedItemsClick,
    handleSelectAllCuratedItemsClick,
  ]);

  // Track the options that SearchableList has had a render cycle to process.
  // SearchableList's internal `useOptionListNavigation` runs an async resync when
  // it receives a new `options` reference, so there's a one-cycle gap between
  // when we update `searchableListOptions` and when the list has committed
  // those options to the DOM. Comparing the two lets us keep the loading
  // indicator visible across the full pipeline: debounce → API → list render.
  const [renderedSearchOptions, setRenderedSearchOptions] = useState(
    searchableListOptions
  );
  useEffect(() => {
    setRenderedSearchOptions(searchableListOptions);
  }, [searchableListOptions]);

  const isSearchSettling =
    (isSearchInFlight || renderedSearchOptions !== searchableListOptions) &&
    hasActiveSearch;

  const renderEmptyStateLabel = useCallback(() => {
    if (isSearchSettling) {
      return 'Searching...';
    }
    if (hasActiveFilter) {
      return 'No items match your filter';
    }
    return 'No results';
  }, [isSearchSettling, hasActiveFilter]);

  const propertyListItems = useMemo(() => {
    return curatedIds.map((itemId, index) => {
      const cacheKey = getItemLabelCacheKey(itemId);
      const cachedLabel = itemLabelCache.get(cacheKey);

      let label: React.ReactNode;
      if (curatedItemsQueries.length === 0) {
        label = itemId;
      } else if (cachedLabel) {
        label = cachedLabel;
      } else if (isCuratedDataLoading) {
        label = (
          <Skeleton
            variant="text"
            width={180}
            data-automation-id={getDataAutomationId('item-label-loader')}
          />
        );
      } else {
        // Derive label synchronously from query response to avoid the
        // render gap between isLoading flipping false and the useEffect
        // that populates itemLabelCache.
        const matchedItem = curatedItemsById.get(itemId);
        if (matchedItem) {
          const derivedLabel = getItemLabel(
            matchedItem,
            collectionState.scheduledItems
          );
          itemLabelCache.set(cacheKey, derivedLabel);
          label = derivedLabel;
        } else {
          label = 'Deleted Item';
        }
      }

      const matchedItemForDescription = displayField
        ? curatedItemsById.get(itemId)
        : undefined;
      const description = matchedItemForDescription
        ? getFieldDisplayText(
            displayField!,
            matchedItemForDescription.get(displayField!.get('slug')),
            {timezone}
          )
        : null;

      return {
        // Use numeric index as ID to simplify array operations (delete/shift).
        id: String(index),
        label,
        description,
      };
    });
  }, [
    curatedIds,
    itemLabelCache,
    getItemLabelCacheKey,
    curatedItemsQueries,
    isCuratedDataLoading,
    curatedItemsById,
    collectionState.scheduledItems,
    displayField,
    timezone,
  ]);

  const onBindingChange = useCallback(
    (updatedBinding: null | Expression, meta: ExpressionBindMetaPayload) => {
      if (updatedBinding === null) {
        // atm there is no datatype defined for DynamoWrapper so EXPRESSION_BIND_CHANGED cannot reset it to default
        // clearing up value manually We also need to ensure we are purely resetting to a Record, not a Distinct
        const defaultValueExprNoDistinct = matchExpression({
          Distinct: ({value}) => value,
          _: identity,
        })(defaultValueExpr);

        expressionEditorDispatch({
          type: UPDATE_EXPRESSION_ACTION_TYPE,
          payload: {
            newExpression: defaultValueExprNoDistinct,
            meta,
          },
          key: [],
        });
      } else {
        expressionEditorDispatch({
          type: 'EXPRESSION_BIND_CHANGED',
          binding: updatedBinding,
          meta,
          key: [],
        });
      }
    },
    [expressionEditorDispatch, defaultValueExpr]
  );

  const [isCuratedItemSelectorOpen, setIsCuratedItemSelectorOpen] =
    useState(false);

  const handleOpenCuratedItemSelectorClick = useCallback(() => {
    dispatchAnalyticsEvent(
      ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_ITEM_SELECTOR_OPENED
    );
    setIsCuratedItemSelectorOpen(true);
  }, [dispatchAnalyticsEvent]);

  const handleCuratedItemSelectorClose = useCallback(() => {
    dispatchAnalyticsEvent(
      ANALYTICS_EVENTS.COLLECTION_LIST_SETTINGS_CURATED_ITEM_SELECTOR_CLOSED
    );
    setIsCuratedItemSelectorOpen(false);
    // The filter is a picking aid, not saved configuration — drop it when
    // the popover closes.
    resetFilter();
  }, [dispatchAnalyticsEvent, resetFilter]);

  // We need to wrap the PropertyListAddButton in a div with a ref to properly
  // anchor the Spring Popover. This ensures the PopoverContent appears below
  // the button instead of at the top left corner of the viewport.
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  if (ContextualExpressions.isBinding(stripAnn(expr))) {
    return (
      <HStack data-automation-id={getDataAutomationId('curated-group')}>
        <Text
          colorVariant="secondary"
          minWidth={rightSidebarLabelWidth}
          width={rightSidebarLabelWidth}
        >
          {CURATED_SETTINGS_LABEL}
        </Text>

        <ExpressionBinding
          expression={exprAsUnique}
          onChange={onBindingChange}
          bindUIVariant="dot"
          automationLabel="Curated"
          style={{width: '100%'}}
        />
      </HStack>
    );
  }

  return (
    <View data-automation-id={getDataAutomationId('curated-group')}>
      <SpringPopover
        placement="bottom-end"
        maxWidth={RIGHT_SIDEBAR_WIDTH}
        width={RIGHT_SIDEBAR_WIDTH}
        minWidth={RIGHT_SIDEBAR_WIDTH}
        shouldShowMask
        anchorRef={buttonContainerRef.current}
        onOpenChange={handleCuratedItemSelectorClose}
        isOpen={isCuratedItemSelectorOpen}
      >
        <HStack>
          <Text colorVariant="secondary">{CURATED_SETTINGS_LABEL}</Text>

          <ExpressionBinding
            expression={exprAsUnique}
            onChange={onBindingChange}
            bindUIVariant="dot"
            automationLabel="Curated"
            button={(openPopover, targetRef) => (
              <Hitarea>
                <SymbolBindingDot
                  ref={targetRef as React.RefObject<HTMLDivElement>}
                  data-automation-id="binding-button_Curated"
                  clickable
                  onClick={openPopover}
                  style={{
                    position: 'absolute',
                    top: -10,
                    left: -8,
                  }}
                />
              </Hitarea>
            )}
          />

          <SpringSpacer />

          <View ref={buttonContainerRef}>
            <PopoverTrigger>
              <PropertyListAddButton
                tooltip="Add list item"
                data-automation-id={getDataAutomationId('add-item-button')}
                onClick={handleOpenCuratedItemSelectorClick}
              />
            </PopoverTrigger>
          </View>
        </HStack>

        <PopoverContent
          data-automation-id={getDataAutomationId('curated-popover')}
          isSeamless
        >
          <SearchableList
            onChange={(newSelectedItems) => {
              const newIds = Array.isArray(newSelectedItems)
                ? newSelectedItems
                : [];

              dispatchCuratedExpressionUpdate(createCCLExpr(newIds));
            }}
            inputValue={searchInputValue}
            isLoading={isSearchFetching && searchableListOptions.length === 0}
            maxSelections={MAX_CURATED_ITEMS_ALLOWED}
            onInputChange={(event) => setSearchInputValue(event.target.value)}
            options={searchableListOptions}
            renderEmptyStateLabel={renderEmptyStateLabel}
            selectionMode="multiple"
            value={curatedIds}
          >
            <VStack padding="$2" spacing={1}>
              <SearchableListInput
                aria-label="Search for items"
                placeholder="Search items"
                suffix={isSearchSettling ? <Spinner size={12} /> : undefined}
                data-automation-id={getDataAutomationId('search-input')}
              />
            </VStack>

            <Divider orientation="horizontal" elevation={1} />

            {displayableFields.size > 0 && (
              <>
                <View
                  paddingX="$2"
                  paddingY="$1"
                  data-automation-id={getDataAutomationId(
                    'display-field-select'
                  )}
                >
                  <HStack spacing={2} align="center">
                    <Text colorVariant="secondary">Show</Text>
                    <Select
                      aria-label="Show field"
                      options={displayFieldSelectOptions}
                      value={displayFieldSlug ?? DISPLAY_FIELD_NONE_VALUE}
                      onChange={handleDisplayFieldChange}
                      shouldTruncateButtonText
                    />
                  </HStack>
                </View>

                <Divider orientation="horizontal" elevation={1} />
              </>
            )}

            {filterableFields.size > 0 && (
              <>
                <View paddingX="$2" paddingY="$1">
                  <HStack spacing={2} align="center">
                    <Button
                      variant="ghost"
                      aria-expanded={isFilterRowOpen}
                      data-automation-id={getDataAutomationId('filter-toggle')}
                      onClick={handleFilterToggleClick}
                    >
                      Filter
                    </Button>

                    <SpringSpacer />

                    {hasActiveFilter ? (
                      <Button
                        variant="ghost"
                        data-automation-id={getDataAutomationId(
                          'filter-clear-button'
                        )}
                        onClick={handleClearFilterClick}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </HStack>

                  {isFilterRowOpen ? (
                    <VStack spacing={1} paddingTop="$1">
                      {filterConditions.map((condition, index) => {
                        const conditionField = resolveFilterField(
                          condition.fieldSlug
                        );
                        const operatorOptions = conditionField
                          ? getFilterOperatorsForField(conditionField)
                          : [];

                        return (
                          <View
                            key={condition.id}
                            data-automation-id={getDataAutomationId(
                              `filter-condition-${index}`
                            )}
                          >
                            <VStack spacing={1}>
                              <HStack spacing={1} align="center">
                                <View
                                  data-automation-id={getDataAutomationId(
                                    'filter-field-select'
                                  )}
                                  style={{flexGrow: 1, minWidth: 0}}
                                >
                                  <Select
                                    aria-label="Filter field"
                                    placeholder="Choose field"
                                    options={filterFieldSelectOptions}
                                    value={condition.fieldSlug ?? undefined}
                                    onChange={(newValue: string) =>
                                      handleConditionFieldChange(
                                        condition.id,
                                        newValue
                                      )
                                    }
                                    shouldTruncateButtonText
                                  />
                                </View>

                                {filterConditions.length > 1 ? (
                                  <IconButton
                                    Icon={CloseDefaultIcon}
                                    label="Remove filter"
                                    variant="ghost"
                                    data-automation-id={getDataAutomationId(
                                      'filter-remove-button'
                                    )}
                                    onClick={() =>
                                      handleRemoveConditionClick(condition.id)
                                    }
                                  />
                                ) : null}
                              </HStack>

                              <View
                                data-automation-id={getDataAutomationId(
                                  'filter-operator-select'
                                )}
                              >
                                <Select
                                  aria-label="Filter operator"
                                  placeholder="Choose operator"
                                  isDisabled={!conditionField}
                                  options={operatorOptions}
                                  value={condition.operator ?? undefined}
                                  onChange={(newValue: string) =>
                                    updateFilterCondition(condition.id, {
                                      operator:
                                        newValue as CuratedPickerFilterOperator['value'],
                                    })
                                  }
                                  shouldTruncateButtonText
                                />
                              </View>

                              {conditionField ? (
                                <View
                                  data-automation-id={getDataAutomationId(
                                    'filter-value-input'
                                  )}
                                >
                                  {conditionField.get('type') === 'Option' ? (
                                    <Select
                                      aria-label="Filter value"
                                      placeholder="Choose option"
                                      options={getOptionValueSelectOptions(
                                        conditionField
                                      )}
                                      value={condition.value || undefined}
                                      onChange={(newValue: string) =>
                                        updateFilterCondition(condition.id, {
                                          value: newValue,
                                        })
                                      }
                                      shouldTruncateButtonText
                                    />
                                  ) : conditionField.get('type') === 'Bool' ? (
                                    <Select
                                      aria-label="Filter value"
                                      placeholder="Choose option"
                                      options={BOOL_FILTER_VALUE_OPTIONS}
                                      value={condition.value || undefined}
                                      onChange={(newValue: string) =>
                                        updateFilterCondition(condition.id, {
                                          value: newValue,
                                        })
                                      }
                                      shouldTruncateButtonText
                                    />
                                  ) : (
                                    <TextInput
                                      aria-label="Filter value"
                                      type={getFilterValueInputType(
                                        conditionField
                                      )}
                                      value={condition.value}
                                      onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                      ) =>
                                        updateFilterCondition(condition.id, {
                                          value: event.target.value,
                                        })
                                      }
                                    />
                                  )}
                                </View>
                              ) : null}
                            </VStack>
                          </View>
                        );
                      })}

                      <View>
                        <Button
                          variant="ghost"
                          data-automation-id={getDataAutomationId(
                            'filter-add-button'
                          )}
                          onClick={handleAddConditionClick}
                        >
                          + Add filter
                        </Button>
                      </View>
                    </VStack>
                  ) : null}
                </View>

                <Divider orientation="horizontal" elevation={1} />
              </>
            )}

            <View
              paddingX="$2"
              paddingY="$1"
              data-automation-id={getDataAutomationId('select-all-checkbox')}
            >
              <Checkbox
                isChecked={isAllSelected}
                isIndeterminate={isIndeterminate}
                onChange={handleToggleSelectAll}
                label={
                  <Text colorVariant="secondary">
                    {curatedIds.length === 0
                      ? hasActiveSearch || hasActiveFilter
                        ? 'Select all matching'
                        : 'Select all'
                      : `${curatedIds.length} selected`}
                  </Text>
                }
              />
            </View>

            <Divider orientation="horizontal" elevation={1} />

            <SearchableListBox maxHeight="200px" />
          </SearchableList>
        </PopoverContent>
      </SpringPopover>

      <View marginTop="$1">
        <PropertyList
          automationId={getDataAutomationId('curated-list')}
          sortable={!isCuratedDataLoading}
          deletable={!isCuratedDataLoading}
          onItemDeleted={handleDeleteCuratedItemClick}
          onItemDropped={handleReorderCuratedItemDrag}
          items={propertyListItems}
          renderItem={(propertyListItem) => {
            if (!propertyListItem.label) return <></>;

            const itemId = propertyListItem.id;

            return (
              <PropertyListItem
                data-automation-id={getDataAutomationId(
                  `curated-list-item-${itemId}`
                )}
                key={itemId}
                id={itemId}
                // Rows default to a fixed kit height; let two-line rows grow.
                style={
                  propertyListItem.description
                    ? {height: 'auto', paddingTop: 4, paddingBottom: 4}
                    : undefined
                }
                label={
                  <VStack spacing={0}>
                    {typeof propertyListItem.label === 'string' ? (
                      <Text truncateToLines={1}>{propertyListItem.label}</Text>
                    ) : (
                      propertyListItem.label
                    )}
                    {propertyListItem.description ? (
                      <Text colorVariant="secondary" truncateToLines={1}>
                        {propertyListItem.description}
                      </Text>
                    ) : null}
                  </VStack>
                }
                onClick={undefined}
                onMouseOver={noop}
                onMouseOut={noop}
                onFocus={noop}
                onBlur={noop}
                icon={null}
              />
            );
          }}
          style={{maxHeight: '242px', overflowY: 'auto'}}
        />
      </View>
    </View>
  );
};
